// Marks an optional attribute that was explicitly declined, as opposed to one not yet answered.
export const SKIPPED = null;

export function getProductAttributes(product) {
    if (!Array.isArray(product?.attributes)) return [];

    // The API already orders attributes by queue.
    return product.attributes
        .map((attribute) => ({
            attribute_id: Number(attribute.attribute_id ?? attribute.id ?? 0) || null,
            name: attribute.name,
            required: Boolean(attribute.required),
            values: (Array.isArray(attribute.values) ? attribute.values : []).map((value) => ({
                id: Number(value.id),
                value: value.value,
                price: Number(value.price ?? 0),
                duration: Number(value.duration ?? 0),
                incompatible_with_ids: Array.isArray(value.incompatible_with_ids) ? value.incompatible_with_ids.map(Number) : [],
            })),
        }))
        .filter((attribute) => Boolean(attribute.name) && attribute.values.length > 0);
}

export function isAnswered(attribute, selections) {
    return Object.prototype.hasOwnProperty.call(selections ?? {}, attribute.name);
}

export function findSelectedValue(attribute, selections) {
    const selectedValueID = selections?.[attribute.name];
    if (selectedValueID === undefined || selectedValueID === SKIPPED) return null;
    return attribute.values.find((value) => value.id === selectedValueID) ?? null;
}

// Maps each attribute name to a Map of blocked value ID -> names of the selections that block it.
export function buildIncompatibilityMap(attributes = [], selections = {}) {
    const activeSelections = attributes
        .map((attribute) => ({ attribute, value: findSelectedValue(attribute, selections) }))
        .filter((entry) => Boolean(entry.value));

    const blockedByAttribute = {};

    attributes.forEach((attribute) => {
        const blocked = new Map();

        activeSelections.forEach(({ attribute: source, value }) => {
            if (source.name === attribute.name) return;

            value.incompatible_with_ids.forEach((blockedID) => {
                if (!attribute.values.some((candidate) => candidate.id === blockedID)) return;

                const reasons = blocked.get(blockedID) ?? [];
                if (!reasons.includes(value.value)) reasons.push(value.value);
                blocked.set(blockedID, reasons);
            });
        });

        blockedByAttribute[attribute.name] = blocked;
    });

    return blockedByAttribute;
}

export function isAttributeBlocked(attribute, blockedValues) {
    return Boolean(attribute?.values?.length) && attribute.values.every((value) => blockedValues?.has(value.id));
}

// Steps reveal in order: each stays hidden until the one before it is answered.
export function getVisibleAttributes(attributes, selections, blockedByAttribute) {
    const visible = [];

    for (const attribute of attributes) {
        visible.push(attribute);
        // A fully blocked step has nothing to answer, so it must not stall the reveal.
        if (isAttributeBlocked(attribute, blockedByAttribute[attribute.name])) continue;
        if (!isAnswered(attribute, selections)) break;
    }

    return visible;
}

export function canSaveSelections(attributes, selections) {
    const blockedByAttribute = buildIncompatibilityMap(attributes, selections);

    return attributes.every((attribute) => {
        const blockedValues = blockedByAttribute[attribute.name];
        const selected = findSelectedValue(attribute, selections);
        if (selected && blockedValues.has(selected.id)) return false;
        // Required only applies while at least one option can still be combined with the selection.
        if (isAttributeBlocked(attribute, blockedValues)) return true;
        return isAnswered(attribute, selections)
            && (!attribute.required || Boolean(selected));
    });
}

// Re-picking the current answer clears it; other answers survive unless the change rules them out.
export function applySelection(attributes, selections, attributeName, valueID) {
    const next = { ...selections };

    if (isAnswered({ name: attributeName }, selections) && selections[attributeName] === valueID) {
        delete next[attributeName];
    } else {
        next[attributeName] = valueID;
    }

    let conflict = true;
    while (conflict) {
        const blockedByAttribute = buildIncompatibilityMap(attributes, next);
        const blockedAttribute = attributes.find((attribute) => {
            const value = findSelectedValue(attribute, next);
            return attribute.name !== attributeName && value && blockedByAttribute[attribute.name].has(value.id);
        });

        conflict = Boolean(blockedAttribute);
        if (blockedAttribute) delete next[blockedAttribute.name];
    }

    return next;
}

// Rebuilds selections from a booked service; unbooked optional attributes count as skipped.
export function restoreSelections(product, bookedAttributes = []) {
    const bookedByID = new Map(bookedAttributes.map((attribute) => [Number(attribute.attribute_id), Number(attribute.attribute_value_id)]));
    const bookedByName = new Map(bookedAttributes.map((attribute) => [attribute.name ?? attribute.attribute_name, Number(attribute.attribute_value_id)]));
    const selections = {};

    getProductAttributes(product).forEach((attribute) => {
        const valueID = bookedByID.get(attribute.attribute_id) ?? bookedByName.get(attribute.name);

        if (attribute.values.some((value) => value.id === valueID)) {
            selections[attribute.name] = valueID;
        } else if (!attribute.required) {
            selections[attribute.name] = SKIPPED;
        }
    });

    return selections;
}

// `basePrice` and `valuePrices` (value ID -> cents) override catalog prices with the ones stored at booking.
export function getSelectedServiceDetails(product, selections = {}, { basePrice, valuePrices } = {}) {
    if (!product) return null;

    const resolvedBasePrice = Number(basePrice ?? product.base_price ?? 0);
    const baseDuration = Number(product.base_duration ?? 0);
    const selectedAttributes = [];
    const attributeSelection = {};
    let totalPrice = resolvedBasePrice;
    let totalDuration = baseDuration;

    getProductAttributes(product).forEach((attribute) => {
        const value = findSelectedValue(attribute, selections);
        if (!value) return;

        const price = valuePrices?.has(value.id) ? valuePrices.get(value.id) : value.price;

        selectedAttributes.push({
            attribute_name: attribute.name,
            attribute_id: attribute.attribute_id,
            attribute_value: value.value,
            attribute_value_id: value.id,
            duration: value.duration,
            price,
        });
        attributeSelection[attribute.name] = {
            attribute_id: attribute.attribute_id,
            attribute_value_id: value.id,
        };
        totalPrice += price;
        totalDuration += value.duration;
    });

    return {
        product_id: product.id,
        product_name: product.name,
        base_price: resolvedBasePrice,
        base_duration: baseDuration,
        total_price: totalPrice,
        total_duration: totalDuration,
        selected_attributes: selectedAttributes,
        attribute_selection: attributeSelection,
    };
}
